import { captureException } from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { type BaseModalFCProps } from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/dialog';
import { SegmentedControl } from '@/components/ui/segmented-control';
import TextArea from '@/components/ui/TextArea';
import { NetworkError } from '@/error';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { submitSurvey } from '@/utils/analytics';
import { DISCORD_URL } from '@/utils/constants';
import {
  formatInfoSection,
  formatUrlSection,
} from '@/utils/feedback-report-format';
import { handleExternalUrlClick } from '@/utils/platform';

import { SnapshotDumpSelector } from './components/SnapshotDumpSelector';
import {
  FeedbackCalendarIssueAttachments,
  FeedbackCalendarIssueType,
} from './feedback-calendar-issue';
import {
  CALENDAR_DATA_SECTION_TITLE,
  useCalendarIssue,
} from './feedback-calendar-issue.hooks';
import {
  FeedbackFollowUpEmail,
  getFollowUpEmailError,
} from './feedback-follow-up-email';
import {
  type MeetingDebugAttachment,
  uploadMeetingDebugReport,
} from './feedback-meeting-debug-report';
import { useSnapshotDumps } from './feedback-snapshot-dumps.hooks';
import { uploadSyncReport } from './feedback-sync-report';
import { useTodayLogs } from './feedback.hooks';
import { Feedback, type FeedbackType } from './feedback.types';

interface FeedbackModalProps extends BaseModalFCProps {
  initialFeedback?: string;
  initialFeedbackType?: FeedbackType;
  log?: string;
  meetingDebug?: MeetingDebugAttachment;
}

export const FeedbackModal = observer(
  ({
    open,
    initialFeedback = '',
    initialFeedbackType = Feedback.Bug,
    log,
    meetingDebug,
    onOpenChange,
  }: FeedbackModalProps) => {
    const user = userStore.user;
    const [feedback, setFeedback] = useState(initialFeedback);
    const [followUpEmail, setFollowUpEmail] = useState('');
    const [feedbackType, setFeedbackType] =
      useState<FeedbackType>(initialFeedbackType);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attachMeetingDebug, setAttachMeetingDebug] = useState(true);

    const { uploadLogs, logsCount, isUploading, shouldUploadLogs } =
      useTodayLogs({
        log,
        feedbackType,
      });

    const {
      apps: snapshotDumpApps,
      selectedApps: selectedDumpApps,
      toggleApp: toggleDumpApp,
      uploadSelectedDumps,
      isPreparing: isPreparingDumps,
      shouldShow: shouldShowDumpSelector,
    } = useSnapshotDumps({ feedbackType });

    const {
      issueType: calendarIssueType,
      setIssueType: setCalendarIssueType,
      includeLocalEvents,
      setIncludeLocalEvents,
      uploadedFile: calendarUploadedFile,
      setUploadedFile: setCalendarUploadedFile,
      uploadCalendarAttachments,
      isUploading: isUploadingCalendar,
    } = useCalendarIssue({ feedbackType });

    const shouldShowLogUpload =
      log || (shouldUploadLogs && feedbackType === 'bug' && logsCount > 0);
    const followUpEmailError = getFollowUpEmailError(followUpEmail);
    const normalizedFollowUpEmail = followUpEmail.trim().toLowerCase();

    const placeholderText = useMemo(() => {
      switch (feedbackType) {
        case 'bug':
          return 'Tell us about the issue you are facing...';
        case 'feature':
          return 'What feature would you like to see?';
        case 'calendar_issue':
          return 'Describe the calendar or meeting issue you experienced...';
        default:
          return "Tell us what's on your mind...";
      }
    }, [feedbackType]);

    const handleSubmit = async () => {
      if (isSubmitting) return;

      setIsSubmitting(true);

      try {
        if (!feedback.trim().length) throw 'Please enter your feedback.';
        if (followUpEmailError) throw followUpEmailError;

        // Upload logs first and get URLs
        let logUrls: string[] = [];
        if (shouldUploadLogs) {
          try {
            logUrls = await uploadLogs();
          } catch (uploadError) {
            console.error('Failed to upload logs:', uploadError);
            toast.error(
              'Failed to upload logs, but your feedback will still be sent'
            );
          }
        }

        let dumpUrls: string[] = [];
        if (selectedDumpApps.size > 0) {
          try {
            dumpUrls = await uploadSelectedDumps();
            if (dumpUrls.length === 0) {
              toast.error(
                'Failed to prepare snapshot dumps, but your feedback will still be sent'
              );
            }
          } catch (dumpError) {
            console.error('Failed to upload snapshot dumps:', dumpError);
            toast.error(
              'Failed to prepare snapshot dumps, but your feedback will still be sent'
            );
          }
        }

        let calendarS3Keys: string[] = [];
        if (feedbackType === Feedback.CalendarIssue) {
          try {
            calendarS3Keys = await uploadCalendarAttachments();
          } catch (calendarError) {
            console.error('Failed to upload calendar data:', calendarError);
            toast.error(
              'Failed to upload calendar data, but your feedback will still be sent'
            );
          }
        }

        let meetingDebugS3Keys: string[] = [];
        if (meetingDebug && attachMeetingDebug) {
          try {
            meetingDebugS3Keys = await uploadMeetingDebugReport(meetingDebug);
          } catch (meetingDebugError) {
            console.error(
              'Failed to upload meeting debug log:',
              meetingDebugError
            );
            toast.error(
              'Failed to upload the meeting debug log, but your feedback will still be sent'
            );
          }
        }

        let feedbackMessage = feedback.trim();

        if (feedbackType === Feedback.CalendarIssue && calendarIssueType) {
          feedbackMessage = `[Issue type: ${calendarIssueType}]\n\n${feedbackMessage}`;
        }

        if (normalizedFollowUpEmail) {
          feedbackMessage += `\n\n=== Contact Email ===\n${normalizedFollowUpEmail}`;
        }

        feedbackMessage += formatInfoSection();
        feedbackMessage += formatUrlSection(
          'Sync Report',
          await uploadSyncReport()
        );

        // Add log content or URLs to feedback
        if (log) {
          feedbackMessage = `${feedbackMessage}\n\n=== Log ===\n${log}`;
        }
        feedbackMessage += formatUrlSection('Logs', logUrls);

        feedbackMessage += formatUrlSection('Snapshot Dumps', dumpUrls);
        feedbackMessage += formatUrlSection(
          'Meeting Debug Log (S3 key, logs bucket)',
          meetingDebugS3Keys
        );
        feedbackMessage += formatUrlSection(
          CALENDAR_DATA_SECTION_TITLE,
          calendarS3Keys
        );

        // Fire-and-forget survey submission
        submitSurvey('feedback & support', [
          feedbackType,
          user?.email ?? 'anonymous',
          feedbackMessage,
        ]).catch(captureException);

        const { status, message } = await backendApis.feedback.submitFeedback(
          feedbackType,
          feedbackMessage
        );

        if (status === 'error') throw new Error(message);

        onOpenChange(false);

        toast.info("Thanks! We'll get back to you shortly.");
      } catch (error) {
        captureException(error);

        if (typeof error === 'string') {
          toast.error(error);
        } else if (error instanceof NetworkError) {
          toast.error(error.title, {
            description: error.message,
          });
        } else {
          toast.error(
            'Something went wrong while sending your feedback. Please try again later.'
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            <YStack className="gap-4">
              <Dialog.Header>
                <Dialog.Title className="text-lg text-foreground">
                  Feedback & Support
                </Dialog.Title>

                <Dialog.Description className="flex flex-col gap-4 items-start">
                  Need help or have suggestions? Let us know below. We read
                  every message.
                </Dialog.Description>
              </Dialog.Header>
              <YStack className="gap-4 items-start">
                <FeedbackFollowUpEmail
                  userEmail={user?.email}
                  followUpEmail={followUpEmail}
                  setFollowUpEmail={setFollowUpEmail}
                />
                <SegmentedControl.Root
                  value={feedbackType}
                  onValueChange={(value) =>
                    setFeedbackType(value as FeedbackType)
                  }
                >
                  <SegmentedControl.List>
                    <SegmentedControl.Trigger value={Feedback.Bug}>
                      Bug Report
                    </SegmentedControl.Trigger>
                    <SegmentedControl.Trigger value={Feedback.Feature}>
                      Feature Request
                    </SegmentedControl.Trigger>
                    <SegmentedControl.Trigger value={Feedback.CalendarIssue}>
                      Calendar Issue
                    </SegmentedControl.Trigger>
                    <SegmentedControl.Trigger value={Feedback.Other}>
                      Other
                    </SegmentedControl.Trigger>
                  </SegmentedControl.List>
                </SegmentedControl.Root>

                {feedbackType === Feedback.CalendarIssue && (
                  <FeedbackCalendarIssueType
                    issueType={calendarIssueType}
                    onIssueTypeChange={setCalendarIssueType}
                  />
                )}
                <TextArea
                  className="h-48"
                  value={feedback}
                  onChange={(event) => setFeedback(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' &&
                      (event.metaKey || event.ctrlKey)
                    ) {
                      event.preventDefault();
                      if (
                        feedback.trim().length &&
                        !isSubmitting &&
                        !followUpEmailError
                      ) {
                        void handleSubmit();
                      }
                    }
                  }}
                  placeholder={placeholderText}
                  autoFocus
                />
                {feedbackType === Feedback.CalendarIssue && (
                  <FeedbackCalendarIssueAttachments
                    issueType={calendarIssueType}
                    includeLocalEvents={includeLocalEvents}
                    onIncludeLocalEventsChange={setIncludeLocalEvents}
                    uploadedFile={calendarUploadedFile}
                    onFileChange={setCalendarUploadedFile}
                    isUploading={isUploadingCalendar}
                  />
                )}
                <XStack
                  className={cn(
                    'items-center gap-1 text-xs text-foreground-muted h-4',
                    isUploading && 'animate-pulse'
                  )}
                >
                  {shouldShowLogUpload && (
                    <>
                      <Icons.textFile className="size-4" />
                      <span>
                        {log
                          ? 'Log file attached'
                          : !isUploading &&
                            `${logsCount} log file${logsCount !== 1 ? 's' : ''} will be uploaded`}
                        {isUploading &&
                          `Uploading ${logsCount} log file${logsCount > 1 ? 's' : ''}...`}
                      </span>
                    </>
                  )}
                </XStack>
                {meetingDebug && (
                  <XStack className="items-center gap-2">
                    <Checkbox
                      id="attach-meeting-debug"
                      checked={attachMeetingDebug}
                      onCheckedChange={(checked) =>
                        setAttachMeetingDebug(checked === true)
                      }
                    />
                    <label
                      htmlFor="attach-meeting-debug"
                      className="text-sm text-foreground-muted cursor-pointer select-none"
                    >
                      Attach the meeting debug log for {meetingDebug.title}
                    </label>
                  </XStack>
                )}
                {shouldShowDumpSelector && (
                  <SnapshotDumpSelector
                    apps={snapshotDumpApps}
                    selected={selectedDumpApps}
                    onToggle={toggleDumpApp}
                    isPreparing={isPreparingDumps}
                  />
                )}
              </YStack>

              <Dialog.Footer className="flex flex-row justify-between sm:justify-between">
                <Button
                  type="button"
                  variant="secondary"
                  className="gap-2 items-center"
                  size="sm"
                  onClick={() => handleExternalUrlClick(DISCORD_URL)}
                >
                  <Icons.discord className="w-4 h-4 fill-[#7950F2]" />
                  <p className="text-xs text-foreground truncate">
                    Join us on Discord
                  </p>
                </Button>
                <XStack className="gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="default"
                    disabled={
                      !feedback.trim().length ||
                      isSubmitting ||
                      Boolean(followUpEmailError)
                    }
                    isLoading={isSubmitting}
                  >
                    Submit
                  </Button>
                </XStack>
              </Dialog.Footer>
            </YStack>
          </form>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);
